import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import CompatibilityRing from "../components/CompatibilityRing";
import VerifiedBadge, { isVerified } from "../components/VerifiedBadge";
import PremiumBadge from "../components/PremiumBadge";
import AffinityBadge from "../components/AffinityBadge";
import SintoniaBadge from "../components/SintoniaBadge";
import LikeQuota from "../components/LikeQuota";
import PromoBanner from "../components/PromoBanner";
import WelcomeTokensModal from "../components/WelcomeTokensModal";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { Heart, X, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDER = "https://images.pexels.com/photos/8629727/pexels-photo-8629727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Discover = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [quotaTick, setQuotaTick] = useState(0);
  const [minAffinity, setMinAffinity] = useState(0);
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    if (user && !user.onboarding_complete) navigate("/onboarding");
  }, [user, navigate]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (minAffinity > 0) params.set("min_affinity", String(minAffinity));
      const res = await client.get(`/discover${params.toString() ? "?" + params.toString() : ""}`);
      setResults(res.data.results);
      setIdx(0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar.");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchResults(); }, [minAffinity]);

  const current = results[idx];

  const onLike = async () => {
    if (!current || actioning) return;
    setActioning(true);
    try {
      const res = await client.post("/like", { target_user_id: current.profile.id });
      if (res.data.match) {
        toast.success(`Ligação mútua com ${current.profile.name}! Vê em Ligações.`);
      } else {
        toast.success(`Demonstraste interesse em ${current.profile.name}. Se ele/ela também gostar, aparecerá nas tuas Ligações.`);
      }
      setQuotaTick((x) => x + 1);
      setIdx(idx + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(err?.response?.data?.detail || "Limite diário atingido.");
        setQuotaTick((x) => x + 1);
        setShowPromo(true);
      } else {
        toast.error("Falha ao gostar.");
      }
    } finally { setActioning(false); }
  };

  const onPass = async () => {
    if (!current || actioning) return;
    setActioning(true);
    try {
      await client.post("/pass", { target_user_id: current.profile.id });
      setIdx(idx + 1);
    } catch (err) {
      toast.error("Falha ao passar.");
    } finally { setActioning(false); }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="discover-page">
      <Navbar />
      <WelcomeTokensModal />
      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-10">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <span className="label-uppercase">Descobrir</span>
              <h1 className="font-display text-3xl md:text-5xl mt-3">As tuas ligações com afinidade</h1>
              <p className="text-[#A29F9B] mt-3 max-w-2xl">
                Ordenadas pela compatibilidade do teu perfil. Toma o tempo que precisares — aqui ninguém faz <em>swipe</em> apressado.
              </p>
            </div>
            <LikeQuota refreshKey={quotaTick} />
          </div>

          {/* Affinity filter */}
          <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="affinity-filter">
            <span className="text-xs text-[#A29F9B] uppercase tracking-wider mr-1">Afinidade mínima:</span>
            {[
              { v: 0,  label: "Todos" },
              { v: 50, label: "Média ou +" },
              { v: 80, label: "Apenas grande" },
            ].map((opt) => (
              <button key={opt.v}
                onClick={() => setMinAffinity(opt.v)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  minAffinity === opt.v
                    ? "bg-[#C5A880]/15 border-[#C5A880] text-[#C5A880]"
                    : "bg-transparent border-white/10 text-[#DCD7D2] hover:border-white/30"
                }`}
                data-testid={`filter-affinity-${opt.v}`}>
                {opt.label}
                {opt.v > 0 && !user?.is_premium && (
                  <span className="ml-1 text-[#C5A880]/70">👑</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="text-[#A29F9B]" data-testid="discover-loading">A carregar perfis...</div>}

        {!loading && results.length === 0 && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="discover-empty">
            <Sparkles className="w-8 h-8 text-[#C5A880] mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">Sem novos perfis para já</h3>
            <p className="text-[#A29F9B]">Volta em breve — estamos a calibrar afinidades para ti.</p>
          </div>
        )}

        {!loading && current && (
          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            <div className="relative rounded-2xl overflow-hidden border border-white/5 aspect-[3/4] bg-[#161920]" data-testid="discover-photo">
              <img
                src={current.profile.photos?.[0] || PLACEHOLDER}
                alt={current.profile.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C0E12] via-transparent to-transparent" />
              <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                {isVerified(current.profile) && <VerifiedBadge size="sm" />}
                {current.profile.is_premium && <PremiumBadge />}
              </div>
              <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                <AffinityBadge tier={current.tier} score={current.compatibility?.overall} size="sm" />
                {current.astral && <SintoniaBadge score={current.astral.score} size="sm" />}
              </div>
              <div className="absolute bottom-0 inset-x-0 p-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display text-3xl md:text-4xl text-white">
                    {current.profile.name}<span className="text-[#C5A880]">, {current.profile.age}</span>
                  </h2>
                </div>
                {current.profile.location && (
                  <p className="text-sm text-[#DCD7D2] mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {current.profile.location}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-[#161920] border border-white/5 rounded-2xl p-8 flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <CompatibilityRing value={current.compatibility.overall} size={120} />
                <div className="text-right space-y-1">
                  <Row label="Personalidade" value={current.compatibility.personality} />
                  <Row label="Valores" value={current.compatibility.values} />
                  <Row label="Sonhos & visão" value={current.compatibility.vision} />
                  <Row label="Mental & espiritual" value={current.compatibility.mental_spiritual} />
                  <Row label="Físico" value={current.compatibility.physical} />
                  <Row label="Estilo de vida" value={current.compatibility.lifestyle} />
                </div>
              </div>

              <div className="mb-6">
                <span className="label-uppercase">Bio</span>
                <p className="mt-2 text-[#DCD7D2] leading-relaxed" data-testid="discover-bio">
                  {current.profile.bio || <span className="italic text-[#A29F9B]">Sem biografia.</span>}
                </p>
              </div>

              {current.profile.interests?.length > 0 && (
                <div className="mb-6">
                  <span className="label-uppercase">Interesses</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {current.profile.interests.map((i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-sm">{i}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4 mt-auto">
                <button onClick={onPass} disabled={actioning}
                  data-testid="btn-pass"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/10 bg-transparent text-[#A29F9B] hover:bg-white/5 transition-colors">
                  <X className="w-5 h-5" /> Passar
                </button>
                <button onClick={onLike} disabled={actioning}
                  data-testid="btn-like"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#C5A880] text-[#0C0E12] font-semibold hover:bg-[#DCC19A] transition-colors">
                  <Heart className="w-5 h-5" /> Conectar
                </button>
              </div>

              <button onClick={()=>navigate(`/profile/${current.profile.id}`)}
                className="mt-3 text-sm text-[#C5A880] hover:underline self-center"
                data-testid="btn-view-profile">
                Ver perfil completo & análise IA
              </button>
            </div>
          </div>
        )}

        {!loading && results.length > 0 && idx >= results.length && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="discover-end">
            <h3 className="font-display text-2xl mb-2">Viste todas as ligações de hoje</h3>
            <p className="text-[#A29F9B] mb-6">Volta em breve para ver novas afinidades.</p>
            <button onClick={fetchResults} className="btn-secondary" data-testid="btn-refresh">Recarregar</button>
          </div>
        )}
      </main>
      {!user?.is_premium && <PromoBanner trigger={showPromo} />}
      <Footer />
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center gap-3 justify-end">
    <span className="text-xs text-[#A29F9B]">{label}</span>
    <span className="font-display text-lg text-[#C5A880] w-10">{value}%</span>
  </div>
);

export default Discover;
